import { useEffect, useState, useContext, useMemo, useRef } from 'react';
import { Box, Stack, Tabs, Tab, Container } from '@mui/material';
import DashboardTable from './DashboardTable';
import SearchPanel from './SearchPanel';
import { dashboardStatusCount, getShipmentStatus, getCompanyList } from '../../../api/dashboard';
import { getAccountDetails } from '../../../api/settings';
import { getStatusReference } from '../../../api/util';
import SnackbarManager from './SnackbarManager';
import { StoreContext } from '../../../components/Store';
import { READ_ONLY_LABEL } from '../../../utils/constants';
import DisplayBalance from '../../../components/DisplayBalance/DisplayBalance';
import { isEORIUser, is360PayStaff } from '../../../utils/roles';

const defaultPaginationSettings = { page: 0, pageSize: 50 };

export default function Dashboard() {
  const store = useContext(StoreContext);
  const [loading, setLoading] = useState(false);

  const [companyIsReadOnly, setCompanyIsReadOnly] = useState(false);
  const [shipments, setShipments] = useState([]);
  const [shipmentsTotalCount, setShipmentsTotalCount] = useState([]);
  const [shipmentStatusCounts, setShipmentStatusCounts] = useState([]);
  const [shipmentStatusIds, setShipmentStatusIds] = useState([]);
  const [selectedCompanyName, setSelectedCompanyName] = useState('ALL');
  const [companies, setCompanies] = useState([]);

  // Get dashboard filters from context
  const [dashboardFilters, setDashboardFilters] = store.dashboardFilters;

  // Initialize searchFilter from context or defaults
  const [searchFilter, setSearchFilter] = useState(() => {
    return Object.keys(dashboardFilters).length > 0
      ? dashboardFilters
      : {
          clientJobNumber: '',
          vehicleIdentity: '',
          lrnMRN: '',
          status: '',
          companyId: null,
          reference: '',
          consignmentRef: '',
          departureStartDate: null,
          departureEndDate: null,
          arrivalStartDate: null,
          arrivalEndDate: null,
        };
  });

  const [paginationSettings, setPaginationSettings] = useState(defaultPaginationSettings);

  const companyId = sessionStorage.getItem('companyid');

  // Sync local searchFilter with context when it changes
  useEffect(() => {
    setDashboardFilters(searchFilter);
  }, [searchFilter, setDashboardFilters]);

  // Update local searchFilter when context changes (e.g., from other components)
  useEffect(() => {
    if (dashboardFilters && Object.keys(dashboardFilters).length > 0) {
      setSearchFilter(dashboardFilters);
    }
  }, [dashboardFilters]);

  const getShipmentData = async (filter, pagination) => {
    try {
      const currentFilter = filter || searchFilter;
      const currentPagination = pagination || paginationSettings;

      // Calculate server pagination with the most up-to-date values
      const serverPagination = calcPaginationForServer(currentPagination);

      // Update state first if new pagination was provided
      if (pagination) {
        setPaginationSettings((prev) => {
          const newSettings = { ...prev, ...pagination };
          console.log('Updating pagination to:', newSettings);
          return newSettings;
        });
      }

      const response = await getShipmentStatus({
        ...currentFilter,
        ...serverPagination,
      });

      setShipments(response.data.filter((row) => row?.headerId));
      setShipmentsTotalCount(response.totalItems);
    } catch (err) {
      store.showError(err.message);
    }
  };

  const calcPaginationForServer = (pagination) => {
    // Always use the pagination parameter, not the state directly
    const pageSize = pagination.pageSize;
    const page = pagination.page;

    return {
      amount: pageSize,
      skip: page * pageSize,
    };
  };

  const errorsInShipments = useMemo(() => {
    return shipmentStatusCounts.find((s) => s.status == 'Errors')?.statusCount > 0;
  }, [shipmentStatusCounts]);

  const menuMessage = useMemo(() => {
    return errorsInShipments ? 'Action Required' : 'Shipments Overview';
  }, [errorsInShipments]);

  const handlePaginationSettingsChange = (newSettings) => {
    getShipmentData(null, newSettings);
  };

  const getShipmentCounts = async () => {
    await dashboardStatusCount()
      .then((data) => {
        setShipmentStatusCounts(data.data);
      })
      .catch((err) => {
        store.showError(err.message);
      });
  };

  const getCompanies = async () => {
    if (isEORIUser()) {
      await getCompanyList()
        .then((data) => {
          // Get the original company list
          const companyData = [{ companyId: null, companyName: 'ALL' }, ...data.data];
          setCompanies(companyData);

          setSelectedCompanyName(companyData[0].companyName); //Set to ALL
        })
        .catch((err) => {
          store.showError(err.message);
        });
    } else {
      // if not Ops, then set company name from session storage
      setSelectedCompanyName(sessionStorage.getItem('company'));
    }
  };

  const getShipmentStatusIds = async () => {
    getStatusReference().then((data) => {
      if (data) {
        const orderedData = [{ statusId: 1, description: 'ALL' }, ...data];
        // Only set status if it's not already set
        if (!searchFilter.status) {
          const updatedFilter = {
            ...searchFilter,
            status: orderedData[0].statusId,
          };
          setSearchFilter(updatedFilter);
        }
        setShipmentStatusIds(orderedData);
      }
    });
  };

  const getReadOnlyStatus = async () => {
    if (!isEORIUser() && companyId) {
      await getAccountDetails(companyId).then((data) => {
        if (data && data.customerTypeId === 40) {
          setCompanyIsReadOnly(true);
        }
      });
    }

    if (is360PayStaff()) {
      setCompanyIsReadOnly(true);
    }
  };

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      await getShipmentCounts();
      await getCompanies();
      await getShipmentStatusIds();
      await getReadOnlyStatus();
      setLoading(false);
    };
    fetch();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      await getShipmentData();
      setLoading(false);
    };
    if (Object.keys(paginationSettings).length) fetch();
  }, [paginationSettings]);

  return (
    <Container
      maxWidth="xl"
      sx={{
        mb: 6,
      }}
    >
      <div className="display-as-row">
        <SnackbarManager refreshCallback={getShipmentData} />
        <Stack direction="row" gap={2} className="dashboard-wrapper">
          <div className="dashboard-panel">
            <Box
              sx={{
                width: '100%',
                fontSize: '20px',
                fontWeight: 'bold',
                marginBottom: '24px',
              }}
            >
              EU ICS
            </Box>
            {!isEORIUser() && (
              <Tabs
                className={errorsInShipments ? 'dashboard-count-menu error' : 'dashboard-count-menu no-error'}
                orientation="vertical"
                value={0}
                sx={{ pb: 2 }}
              >
                <div
                  className={
                    menuMessage === 'Action Required'
                      ? 'dashboard-count-menu-header error'
                      : 'dashboard-count-menu-header'
                  }
                >
                  {menuMessage}
                </div>
                {!shipmentStatusCounts.length && <Tab label="No shipments found" />}

                {!errorsInShipments &&
                  shipmentStatusCounts.map((tab, index) => {
                    if (!errorsInShipments)
                      return (
                        <Tab
                          className="no-error"
                          key={index}
                          label={tab.statusCount + '  ' + tab.status}
                          disabled
                          sx={{
                            '&.Mui-disabled': {
                              color: '#000',
                              fontSize: '16px',
                              textDecoration: 'none !important', //used !important here, otherwise the underline would still be there
                            },
                          }}
                        />
                      );
                  })}

                {errorsInShipments &&
                  shipmentStatusCounts.map((tab, index) => {
                    if (tab.status.toUpperCase() === 'ERRORS') {
                      return <Tab className="error" key={index} label={tab.statusCount + '  ' + tab.status} disabled />;
                    }
                  })}
              </Tabs>
            )}
            <SearchPanel
              companies={companies}
              getShipmentData={getShipmentData}
              searchFilter={searchFilter}
              setSearchFilter={setSearchFilter}
              setSelectedCompanyName={setSelectedCompanyName}
              shipmentStatusIds={shipmentStatusIds}
            />
          </div>

          <div className="dashboard-table-wrapper-wrapper">
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '20px',
                fontWeight: 'bold',
              }}
            >
              <Box>
                Shipping Status -{' '}
                {!selectedCompanyName || selectedCompanyName === 'ALL'
                  ? 'EORI'
                  : selectedCompanyName || sessionStorage.getItem('company')}
                {companyIsReadOnly && <>{READ_ONLY_LABEL}</>}
              </Box>
              {store.isICS2DirectCompany() && (
                <DisplayBalance balance={store.currentBalance[0]} companyId={companyId} />
              )}
            </Box>
            <div role="tabpanel" id={`vertical-tabpanel-${0}`} className="dashboard-table-wrapper">
              <DashboardTable
                loading={loading}
                shipments={shipments}
                shipmentsTotalCount={shipmentsTotalCount}
                paginationSettings={paginationSettings}
                handlePaginationSettingsChange={handlePaginationSettingsChange}
              />
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'end',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  paddingTop: '15px',
                }}
              >
                <div>Results : {shipmentsTotalCount}</div>
              </Box>
            </div>
          </div>
        </Stack>
      </div>
    </Container>
  );
}
